import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, ArchiveRestore, MapPin, Pencil } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { OFFER_TYPES } from "../../constants";
import { OfferStatus } from "../RepresentativeOffers/components/OfferCard/interfaces";
import RepresentativesTable, {
  type TraderRepresentativeItem,
} from "./components/RepresentativesTable";

type EntityId = number | string;

export interface TraderDetailsItem {
  accepted_count?: number | null;
  active?: boolean | null;
  country?: {
    name?: string | null;
    [key: string]: unknown;
  } | null;
  direct_offers_count?: number | null;
  exchange_done_count?: number | null;
  id?: EntityId | null;
  location_details?: {
    name?: string | null;
    [key: string]: unknown;
  } | null;
  location_name?: string | null;
  market_offers_count?: number | null;
  name?: string | null;
  physical_address?: string | null;
  [key: string]: unknown;
}

export interface TraderOfferItem {
  id?: EntityId | null;
  offer_type?: string | null;
  status?: OfferStatus | string | null;
  tagged_aggregator?: {
    id?: EntityId | null;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

export interface TraderDetailsModalProps {
  canManage?: boolean;
  isArchiving?: boolean;
  isUnarchiving?: boolean;
  isFetchingTraderRepresentatives?: boolean;
  isOpen?: boolean;
  offers?: TraderOfferItem[];
  onArchive?: (trader: TraderDetailsItem) => Promise<unknown>;
  onUnarchive?: (trader: TraderDetailsItem) => Promise<unknown>;
  onClickEdit?: (trader: TraderDetailsItem) => void;
  onValidate?: () => void;
  representatives?: TraderRepresentativeItem[];
  trader?: TraderDetailsItem;
}

interface TraderStat {
  label: string;
  value: number;
}

interface TraderDetailsModalContent {
  isFetchingTraderRepresentatives: boolean;
  offers: TraderOfferItem[];
  representatives: TraderRepresentativeItem[];
  trader: TraderDetailsItem;
}

const EMPTY_TRADER: TraderDetailsItem = {};
const EMPTY_OFFERS: TraderOfferItem[] = [];
const EMPTY_REPRESENTATIVES: TraderRepresentativeItem[] = [];

const getTraderId = (trader: TraderDetailsItem) =>
  trader.id === undefined || trader.id === null ? undefined : trader.id;

const getTraderOffers = (offers: TraderOfferItem[], trader: TraderDetailsItem) => {
  const traderId = getTraderId(trader);

  if (traderId === undefined) {
    return [];
  }

  return offers.filter(offer => offer.tagged_aggregator?.id === traderId);
};

const getFiniteCount = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const hasActivityCounts = (trader: TraderDetailsItem) =>
  [trader.direct_offers_count, trader.market_offers_count, trader.exchange_done_count].some(
    count => getFiniteCount(count) !== undefined
  );

const createTraderStatsFromActivity = (trader: TraderDetailsItem): TraderStat[] => [
  {
    label: "aggregatorDetails.aggregatorStats.depositPurchaseRequests",
    value: getFiniteCount(trader.direct_offers_count) ?? 0,
  },
  {
    label: "aggregatorDetails.aggregatorStats.traderDemands",
    value: getFiniteCount(trader.market_offers_count) ?? 0,
  },
  {
    label: "aggregatorDetails.aggregatorStats.offersExchanged",
    value: getFiniteCount(trader.exchange_done_count) ?? 0,
  },
];

const createTraderStatsFromOffers = (offers: TraderOfferItem[]): TraderStat[] => [
  {
    label: "aggregatorDetails.aggregatorStats.depositPurchaseRequests",
    value: offers.filter(offer => offer.offer_type === OFFER_TYPES.DIRECT_OFFER).length,
  },
  {
    label: "aggregatorDetails.aggregatorStats.traderDemands",
    value: offers.filter(offer => offer.offer_type === OFFER_TYPES.MARKET_OFFER).length,
  },
  {
    label: "aggregatorDetails.aggregatorStats.offersExchanged",
    value: offers.filter(offer => offer.status === OfferStatus.ExchangeDone).length,
  },
];

const createTraderStats = (trader: TraderDetailsItem, offers: TraderOfferItem[]) =>
  hasActivityCounts(trader)
    ? createTraderStatsFromActivity(trader)
    : createTraderStatsFromOffers(offers);

const getAddressParts = (trader: TraderDetailsItem) =>
  [
    trader.physical_address,
    trader.location_details?.name ?? trader.location_name,
    trader.country?.name,
  ].filter((part): part is string => Boolean(part));

const TraderStats = ({ isLoading, stats }: { isLoading: boolean; stats: TraderStat[] }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex min-h-20 items-center justify-center rounded-md border border-border bg-muted/30">
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    );
  }

  return (
    <section aria-label={t("aggregatorDetails.aggregatorStats.heading")} className="space-y-3">
      <h3 className="font-semibold text-foreground text-sm">
        {t("aggregatorDetails.aggregatorStats.heading")}
      </h3>
      <dl className="grid gap-3 sm:grid-cols-3">
        {stats.map(stat => (
          <div key={stat.label} className="rounded-md border border-border bg-muted/30 p-3">
            <dt className="text-muted-foreground text-sm">{t(stat.label)}</dt>
            <dd className="mt-1 font-semibold text-2xl text-foreground tabular-nums">
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

interface TraderArchiveDialogProps {
  addressParts: string[];
  isArchiving: boolean;
  name: string;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const TraderArchiveDialog = ({
  addressParts,
  isArchiving,
  name,
  onConfirm,
  onOpenChange,
  open,
}: TraderArchiveDialogProps) => {
  const { t } = useTranslation();
  const detailRows = [
    {
      label: t("traderDetails.archiveDialog.address"),
      value: addressParts.length ? addressParts.join(", ") : t("common.notAvailable"),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("traderDetails.archiveDialog.title")}</DialogTitle>
          <DialogDescription>{t("traderDetails.archiveDialog.description")}</DialogDescription>
        </DialogHeader>

        <dl className="grid gap-3 rounded-lg border border-neutral-alpha-300 bg-muted/20 p-4">
          <div className="grid gap-1">
            <dt className="font-medium text-muted-foreground text-xs">
              {t("traderDetails.archiveDialog.name")}
            </dt>
            <dd className="text-sm">{name}</dd>
          </div>
          {detailRows.map(row => (
            <div key={row.label} className="grid gap-1">
              <dt className="font-medium text-muted-foreground text-xs">{row.label}</dt>
              <dd className="text-sm">{row.value}</dd>
            </div>
          ))}
        </dl>

        <DialogFooter>
          <DialogClose asChild>
            <WFPButton type="button" variant="outline">
              {t("common.cancel")}
            </WFPButton>
          </DialogClose>
          <WFPButton type="button" variant="destructive" onClick={onConfirm} disabled={isArchiving}>
            <Archive className="size-4" aria-hidden="true" />
            {t("common.archive")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TraderReactivateDialogProps {
  addressParts: string[];
  isUnarchiving: boolean;
  name: string;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const TraderReactivateDialog = ({
  addressParts,
  isUnarchiving,
  name,
  onConfirm,
  onOpenChange,
  open,
}: TraderReactivateDialogProps) => {
  const { t } = useTranslation();
  const detailRows = [
    {
      label: t("traderDetails.reactivateDialog.address"),
      value: addressParts.length ? addressParts.join(", ") : t("common.notAvailable"),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("traderDetails.reactivateDialog.title")}</DialogTitle>
          <DialogDescription>{t("traderDetails.reactivateDialog.description")}</DialogDescription>
        </DialogHeader>

        <dl className="grid gap-3 rounded-lg border border-neutral-alpha-300 bg-muted/20 p-4">
          <div className="grid gap-1">
            <dt className="font-medium text-muted-foreground text-xs">
              {t("traderDetails.reactivateDialog.name")}
            </dt>
            <dd className="text-sm">{name}</dd>
          </div>
          {detailRows.map(row => (
            <div key={row.label} className="grid gap-1">
              <dt className="font-medium text-muted-foreground text-xs">{row.label}</dt>
              <dd className="text-sm">{row.value}</dd>
            </div>
          ))}
        </dl>

        <DialogFooter>
          <DialogClose asChild>
            <WFPButton type="button" variant="outline">
              {t("common.cancel")}
            </WFPButton>
          </DialogClose>
          <WFPButton type="button" variant="success" onClick={onConfirm} disabled={isUnarchiving}>
            <ArchiveRestore className="size-4" aria-hidden="true" />
            {t("common.reactivate")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TraderDetailsModal = ({
  canManage = true,
  isArchiving = false,
  isUnarchiving = false,
  isFetchingTraderRepresentatives = false,
  isOpen = false,
  offers: allOffers = EMPTY_OFFERS,
  onArchive,
  onUnarchive,
  onClickEdit,
  onValidate,
  representatives = EMPTY_REPRESENTATIVES,
  trader = EMPTY_TRADER,
}: TraderDetailsModalProps) => {
  const { t } = useTranslation();
  const [isArchiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isReactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const currentContent = useMemo<TraderDetailsModalContent>(
    () => ({
      isFetchingTraderRepresentatives,
      offers: allOffers,
      representatives,
      trader,
    }),
    [allOffers, isFetchingTraderRepresentatives, representatives, trader]
  );
  // Radix keeps content mounted during the close animation; keep that frame visually stable.
  const lastOpenContentRef = useRef(currentContent);

  if (isOpen) {
    lastOpenContentRef.current = currentContent;
  }

  const visibleContent = isOpen ? currentContent : lastOpenContentRef.current;
  const traderName = visibleContent.trader.name || t("traders.title");
  const addressParts = useMemo(
    () => getAddressParts(visibleContent.trader),
    [visibleContent.trader]
  );
  const traderOffers = useMemo(
    () => getTraderOffers(visibleContent.offers, visibleContent.trader),
    [visibleContent.offers, visibleContent.trader]
  );
  const stats = useMemo(
    () => createTraderStats(visibleContent.trader, traderOffers),
    [traderOffers, visibleContent.trader]
  );

  const onOpenChange = (nextIsOpen: boolean) => {
    if (!nextIsOpen) {
      onValidate?.();
    }
  };

  const isArchived = visibleContent.trader.active === false;

  const handleArchive = async () => {
    const result = await onArchive?.(visibleContent.trader);
    if (result) {
      setArchiveDialogOpen(false);
    }
  };

  const handleReactivate = async () => {
    const result = await onUnarchive?.(visibleContent.trader);
    if (result) {
      setReactivateDialogOpen(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader className="gap-3 pr-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-2">
                <DialogTitle className="truncate text-2xl">{traderName}</DialogTitle>
                <DialogDescription asChild>
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    <span>
                      {addressParts.length
                        ? addressParts.join(", ")
                        : t("common.notAvailable", "Not available")}
                    </span>
                  </div>
                </DialogDescription>
              </div>
              {canManage &&
                (isArchived ? (
                  <div className="flex shrink-0 items-center gap-2">
                    <WFPButton
                      type="button"
                      variant="success-secondary"
                      size="sm"
                      onClick={() => setReactivateDialogOpen(true)}
                    >
                      <ArchiveRestore className="size-4" aria-hidden="true" />
                      {t("common.reactivate")}
                    </WFPButton>
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-2">
                    {onClickEdit ? (
                      <WFPButton
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onClickEdit(visibleContent.trader)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                        {t("common.edit")}
                      </WFPButton>
                    ) : null}
                    <WFPButton
                      type="button"
                      variant="destructive-secondary"
                      size="sm"
                      onClick={() => setArchiveDialogOpen(true)}
                    >
                      <Archive className="size-4" aria-hidden="true" />
                      {t("common.archive")}
                    </WFPButton>
                  </div>
                ))}
            </div>
          </DialogHeader>

          <div className="space-y-6">
            <RepresentativesTable
              representatives={visibleContent.representatives}
              isFetchingRepresentatives={visibleContent.isFetchingTraderRepresentatives}
            />
            <TraderStats isLoading={visibleContent.isFetchingTraderRepresentatives} stats={stats} />
          </div>

          <DialogFooter>
            <WFPButton type="button" onClick={onValidate}>
              {t("common.done")}
            </WFPButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TraderArchiveDialog
        addressParts={addressParts}
        isArchiving={isArchiving}
        name={traderName}
        onConfirm={handleArchive}
        onOpenChange={setArchiveDialogOpen}
        open={isArchiveDialogOpen}
      />
      <TraderReactivateDialog
        addressParts={addressParts}
        isUnarchiving={isUnarchiving}
        name={traderName}
        onConfirm={handleReactivate}
        onOpenChange={setReactivateDialogOpen}
        open={isReactivateDialogOpen}
      />
    </>
  );
};

export default TraderDetailsModal;
